import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  TrendingUp, AlertTriangle, ArrowUpRight, ArrowDownRight, Bitcoin,
  Building2, Globe2, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight,
  Calendar, Loader2, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Brand
const BRAND_TEAL = '#00CED1';

// ---------------------------------------------------------------------------
// PublicPredictions — read-only landing page for the AI investment-prediction
// tool. Visitors get: hero with overall win-rate, 7d/30d win-rate cards, a
// date selector to browse historical batches, and the picks for the selected
// day with current price + Win/Lose outcome.
// ---------------------------------------------------------------------------

const marketLabel = (m) => ({ us: 'US', jse: 'JSE', crypto: 'CRYPTO' }[m] || (m || '').toUpperCase());
const marketIcon = (m) => {
  if (m === 'crypto') return <Bitcoin className="w-3 h-3" />;
  if (m === 'jse') return <Building2 className="w-3 h-3" />;
  return <Globe2 className="w-3 h-3" />;
};

const fmtPrice = (val, ccy) => {
  if (val === null || val === undefined || isNaN(val)) return '—';
  const prefix = ccy === 'ZAR' ? 'R ' : '$';
  const abs = Math.abs(val);
  if (abs < 1 && abs > 0) return `${prefix}${val.toFixed(4)}`;
  return `${prefix}${val.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const OutcomeBadge = ({ outcome }) => {
  if (outcome === 'win') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white"><CheckCircle2 className="w-3 h-3" />WIN</span>;
  if (outcome === 'lose') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500 text-white"><XCircle className="w-3 h-3" />LOSE</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600"><Clock className="w-3 h-3" />OPEN</span>;
};

const WinRateRing = ({ pct, label, sub }) => {
  const display = pct === null || pct === undefined ? '—' : `${Math.round(pct)}%`;
  const color = pct === null || pct === undefined ? '#94a3b8' : pct >= 60 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="text-center" data-testid={`public-pred-ring-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="relative inline-flex w-28 h-28 items-center justify-center rounded-full" style={{ background: `conic-gradient(${color} ${(pct || 0) * 3.6}deg, rgba(255,255,255,0.08) 0deg)` }}>
        <div className="absolute inset-2 rounded-full bg-slate-900 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-white">{display}</span>
          <span className="text-[10px] text-slate-400 uppercase tracking-wide">{sub}</span>
        </div>
      </div>
      <div className="mt-2 text-sm font-semibold text-slate-200">{label}</div>
    </div>
  );
};

const PicksTable = ({ rows, title, testid }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400 text-sm" data-testid={testid}>
        No {title.toLowerCase()} picks for this date.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
        <h3 className="font-bold text-slate-800">{title}</h3>
        <span className="text-xs text-slate-500">{rows.length} picks</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <th className="p-2.5 text-left">Asset</th>
              <th className="p-2.5 text-left">Targets</th>
              <th className="p-2.5 text-right">Current</th>
              <th className="p-2.5 text-center">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const move = r.current_price && r.initial_price
                ? ((r.current_price - r.initial_price) / r.initial_price) * 100 : null;
              return (
                <React.Fragment key={r.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-2.5 align-top">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide bg-teal-700 text-white">
                        {marketIcon(r.market)}{marketLabel(r.market)}
                      </span>
                      <span className="font-mono text-sm font-bold text-slate-800">{r.symbol}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate max-w-[180px]" title={r.display_name}>
                      {r.display_name}
                    </div>
                  </td>
                  <td className="p-2.5 align-top text-xs">
                    <div className="text-slate-700"><span className="text-slate-400">Buy:</span> {fmtPrice(r.buy_price_target, r.currency)}</div>
                    <div className="text-slate-700"><span className="text-slate-400">Sell:</span> {fmtPrice(r.sell_price_target, r.currency)}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{r.buy_date} → {r.sell_date}</div>
                  </td>
                  <td className="p-2.5 align-top text-right text-xs">
                    <div className="font-bold text-sm text-slate-800">{fmtPrice(r.current_price, r.currency)}</div>
                    {move !== null && (
                      <div className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${move >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {move >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {Math.abs(move).toFixed(2)}%
                      </div>
                    )}
                  </td>
                  <td className="p-2.5 align-top text-center">
                    <OutcomeBadge outcome={r.outcome} />
                    {r.outcome_pnl_pct !== null && r.outcome_pnl_pct !== undefined && (
                      <div className={`text-[10px] mt-1 font-semibold ${r.outcome_pnl_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {r.outcome_pnl_pct >= 0 ? '+' : ''}{r.outcome_pnl_pct}%
                      </div>
                    )}
                  </td>
                </tr>
                {/* AI thesis row — why this pick was made */}
                {r.reasoning && (
                  <tr className="bg-slate-50/60" data-testid={`public-pred-reasoning-${r.id}`}>
                    <td colSpan={4} className="px-3 pb-3 pt-0">
                      <div className="flex items-start gap-2 text-[12px] text-slate-600 leading-relaxed">
                        <Sparkles className="w-3.5 h-3.5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <p className="italic">{r.reasoning}</p>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PublicPredictions = () => {
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  const loadInitial = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/predictions/summary`),
        axios.get(`${BACKEND_URL}/api/public/predictions/batches`),
      ]);
      setSummary(s.data);
      setBatches(b.data || []);
      const initial = s.data?.latest_batch_date || (b.data && b.data[0]?.date) || null;
      if (initial) setSelectedDate(initial);
    } catch (err) {
      console.error('load failed', err);
    }
  }, []);

  const loadBatch = useCallback(async (d) => {
    if (!d) return;
    setLoadingBatch(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/predictions/batch/${d}`);
      setBatch(r.data);
    } catch (err) {
      console.error('batch load failed', err);
      setBatch(null);
    } finally {
      setLoadingBatch(false);
    }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);
  useEffect(() => { if (selectedDate) loadBatch(selectedDate); }, [selectedDate, loadBatch]);

  const currentIdx = batches.findIndex((b) => b.date === selectedDate);
  const goPrev = () => {
    // Older date = larger index (sorted newest first)
    if (currentIdx + 1 < batches.length) setSelectedDate(batches[currentIdx + 1].date);
  };
  const goNext = () => {
    if (currentIdx > 0) setSelectedDate(batches[currentIdx - 1].date);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-predictions-page">
      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 mb-3">
            <Link to="/" className="text-xs text-slate-400 hover:text-white">← Brainwave Architectural Projects</Link>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-7 h-7" style={{ color: BRAND_TEAL }} />
            <h1 className="text-3xl sm:text-4xl font-bold">Pre-Invest · AI Daily Watchlist</h1>
          </div>
          <p className="text-slate-300 max-w-2xl">
            Every morning at 06:00 SAST, Gemini 3 Pro screens US equities, the JSE and the top-25 crypto
            markets — then publishes <b>20 picks</b> (10 long-term + 10 short-term) with buy/sell targets,
            stop-loss levels and a thesis grounded in the day's headlines. Track each pick's
            outcome live below.
          </p>

          {/* Win-rate rings */}
          {summary && (
            <div className="mt-8 flex flex-wrap gap-8 items-end" data-testid="public-pred-summary">
              <WinRateRing pct={summary.win_rate_all_time_pct} label="All-time" sub={`${summary.wins_all_time}/${summary.wins_all_time + summary.losses_all_time}`} />
              <WinRateRing pct={summary.window_30d?.win_rate_pct} label="Last 30 days" sub={`${summary.window_30d?.wins}/${summary.window_30d?.wins + summary.window_30d?.losses}`} />
              <WinRateRing pct={summary.window_7d?.win_rate_pct} label="Last 7 days" sub={`${summary.window_7d?.wins}/${summary.window_7d?.wins + summary.window_7d?.losses}`} />
              <div className="ml-auto text-right">
                <div className="text-xs uppercase tracking-wide text-slate-400">Latest batch</div>
                <div className="text-xl font-bold mt-1" style={{ color: BRAND_TEAL }}>{summary.latest_batch_date || 'No picks yet'}</div>
                <div className="text-xs text-slate-400 mt-1">{summary.total_picks} total predictions</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Disclaimer */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-xs" data-testid="public-pred-disclaimer">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span><b>AI-generated commentary — not financial advice.</b> Predictions are produced by Gemini 3 Pro from public market data and news headlines. We make no guarantees of accuracy or future returns. Always verify before acting and never risk capital you can't afford to lose.</span>
        </div>

        {/* Date picker */}
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="public-pred-datepicker">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Calendar className="w-4 h-4" />
              <span className="font-semibold">Historical recall</span>
              <span className="text-xs text-slate-400">{batches.length} batch{batches.length === 1 ? '' : 'es'} available</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goPrev}
                disabled={currentIdx + 1 >= batches.length}
                className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-30"
                data-testid="public-pred-prev"
                aria-label="Older batch"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <select
                value={selectedDate || ''}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-1.5 rounded border border-slate-200 bg-white text-sm font-mono"
                data-testid="public-pred-date-select"
              >
                {batches.length === 0 && <option value="">No data</option>}
                {batches.map((b) => (
                  <option key={b.date} value={b.date}>
                    {b.date} · {b.win_rate_pct !== null ? `${Math.round(b.win_rate_pct)}% win` : 'open'} · {b.total} picks
                  </option>
                ))}
              </select>
              <button
                onClick={goNext}
                disabled={currentIdx <= 0}
                className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-30"
                data-testid="public-pred-next"
                aria-label="Newer batch"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mini-trend strip — last 14 days */}
          {batches.length > 0 && (
            <div className="flex gap-0.5 mt-2 items-end h-10" data-testid="public-pred-trend">
              {batches.slice(0, 21).slice().reverse().map((b) => {
                const wr = b.win_rate_pct;
                const h = wr === null || wr === undefined ? 8 : Math.max(8, (wr / 100) * 40);
                const color = wr === null || wr === undefined ? '#cbd5e1' : wr >= 60 ? '#10b981' : wr >= 40 ? '#f59e0b' : '#ef4444';
                const isSel = b.date === selectedDate;
                return (
                  <button
                    key={b.date}
                    onClick={() => setSelectedDate(b.date)}
                    style={{ height: `${h}px`, backgroundColor: color, opacity: isSel ? 1 : 0.55, width: '14px' }}
                    className={`rounded-sm transition hover:opacity-100 ${isSel ? 'ring-2 ring-teal-500' : ''}`}
                    title={`${b.date} · ${wr === null || wr === undefined ? 'open' : `${wr}% win`} · ${b.total} picks`}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Selected batch */}
        {loadingBatch ? (
          <div className="text-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading batch…
          </div>
        ) : batch ? (
          <>
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="public-pred-batch-header">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Batch</div>
                  <h2 className="text-xl font-bold text-slate-800">{batch.date}</h2>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">{batch.total} picks</span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">{batch.wins} wins</span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800">{batch.losses} losses</span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-200 text-slate-700">{batch.open} open</span>
                  {batch.win_rate_pct !== null && batch.win_rate_pct !== undefined && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: BRAND_TEAL }}>
                      {Math.round(batch.win_rate_pct)}% win-rate
                    </span>
                  )}
                </div>
              </div>
              {batch.market_brief && (
                <div className="flex items-start gap-3 mt-3 pt-3 border-t border-slate-100">
                  <Sparkles className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700 leading-relaxed">{batch.market_brief}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <PicksTable rows={batch.long_term} title="Long-term (~90-150 days)" testid="public-pred-long-term" />
              <PicksTable rows={batch.short_term} title="Short-term (~3-10 days)" testid="public-pred-short-term" />
            </div>
          </>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500" data-testid="public-pred-empty">
            <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-700">No predictions yet</h3>
            <p className="text-sm mt-1">Today's batch will be published at 06:00 SAST.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPredictions;
